
import React from 'react';
import { VendorProfile, Ad, Neighborhood, SubscriptionPlan } from '../types';
import AdCard from './AdCard';

interface VendorProfileViewProps {
  vendor: VendorProfile;
  isVerifiedBadge?: boolean;
  ads: Ad[];
  neighborhood: Neighborhood | undefined;
  onBack: () => void;
  onAdClick: (ad: Ad) => void;
}

const VendorProfileView: React.FC<VendorProfileViewProps> = ({ vendor, isVerifiedBadge, ads, neighborhood, onBack, onAdClick }) => {
  return (
    <div className="min-h-screen bg-gray-50 animate-in fade-in duration-500">
      {/* Vendor Header */}
      <header className="bg-navy relative pt-12 pb-24 px-6 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-20 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[100%] bg-mint blur-[120px] rounded-full"></div>
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <button 
            onClick={onBack}
            className="text-mint flex items-center gap-2 font-bold text-xs uppercase tracking-widest mb-10 hover:opacity-80"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7"/></svg>
            Voltar para a Busca
          </button>

          <div className="flex flex-col md:flex-row items-center gap-8 text-center md:text-left">
            <div className="relative">
              <img 
                src={vendor.avatar_url} 
                className="w-32 h-32 md:w-40 md:h-40 rounded-[3rem] object-cover border-4 border-white/10 shadow-2xl" 
                alt={vendor.name} 
              />
              {isVerifiedBadge && (
                <div className="absolute -bottom-2 -right-2 bg-mint text-navy p-2 rounded-2xl shadow-xl border-2 border-navy">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path></svg>
                </div>
              )}
            </div>
            
            <div className="flex-1">
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 mb-3">
                <h1 className="text-3xl md:text-5xl font-title font-black text-white uppercase italic tracking-tighter leading-tight">{vendor.name}</h1>
                <span className="bg-white/10 text-white px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border border-white/5">📍 {neighborhood?.name}</span>
              </div>
              <p className="text-blue-100/70 text-sm md:text-base max-w-xl font-medium leading-relaxed mb-6">
                {vendor.bio}
              </p>
              
              <div className="flex flex-wrap justify-center md:justify-start gap-4">
                <a 
                  href={`https://wa.me/${vendor.whatsapp}`} 
                  target="_blank"
                  className="bg-mint text-navy font-black text-[10px] uppercase tracking-widest px-8 py-4 rounded-2xl shadow-xl shadow-mint/20 hover:scale-105 transition-all"
                >
                  WhatsApp da Loja
                </a>
                {vendor.instagram && (
                  <a 
                    href={`https://instagram.com/${vendor.instagram.replace('@','')}`} 
                    target="_blank"
                    className="bg-white/5 text-white border border-white/10 font-black text-[10px] uppercase tracking-widest px-8 py-4 rounded-2xl hover:bg-white/10 transition-all"
                  >
                    Instagram
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Product List */}
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 -mt-10 relative z-20 pb-20">
        <div className="bg-white p-6 sm:p-10 rounded-[2.5rem] sm:rounded-[3rem] shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-xl font-title font-black text-navy uppercase italic">Vitrine de Ofertas <span className="text-gray-300 ml-2">({ads.length})</span></h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 sm:gap-6">
            {ads.map(ad => (
              <div key={ad.id} onClick={() => onAdClick(ad)} className="h-full">
                <AdCard ad={ad} vendor={vendor} isVerifiedBadge={isVerifiedBadge} neighborhood={neighborhood} />
              </div>
            ))}
          </div>

          {ads.length === 0 && (
            <div className="py-20 text-center">
              <p className="text-gray-400 font-bold uppercase text-xs tracking-widest">Este parceiro ainda não publicou anúncios.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default VendorProfileView;
